import { useEffect, useRef, useState } from 'react';
import { tokens } from '@/design';
import type { LayoutPictureSource, PageRect } from './layoutTypes';
import { pictureNativeSizeMm, placePictureRect } from './layoutPicture';

export interface PlacedLayoutPicture {
  readonly rect: PageRect;
  readonly preserveAspectRatio: boolean;
}

interface Props {
  readonly source: LayoutPictureSource;
  readonly pageWidthMm: number;
  readonly pageHeightMm: number;
  readonly onComplete: (placement: PlacedLayoutPicture) => void;
  readonly onCancel: () => void;
}

const DRAG_THRESHOLD_MM = 2;

export function LayoutPicturePlacementOverlay({
  source,
  pageWidthMm,
  pageHeightMm,
  onComplete,
  onCancel,
}: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<PageRect | null>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    overlayRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      const pane = overlayRef.current?.closest('[data-testid="layout-workspace-pane"]');
      if (pane && !pane.contains(globalThis.document.activeElement)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const toPage = (clientX: number, clientY: number) => {
    const bounds = overlayRef.current?.getBoundingClientRect();
    if (!bounds) return { x: 0, y: 0 };
    return {
      x: ((clientX - bounds.left) / bounds.width) * pageWidthMm,
      y: ((clientY - bounds.top) / bounds.height) * pageHeightMm,
    };
  };

  const native = pictureNativeSizeMm(source.naturalWidthPx, source.naturalHeightPx);

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      role="application"
      aria-label="Place picture on the layout. Click for original size or drag a box. Escape cancels."
      data-testid="layout-picture-placement"
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const origin = toPage(event.clientX, event.clientY);
        dragRef.current = origin;
        setPreview({ x: origin.x, y: origin.y, width: 1, height: 1 });
      }}
      onPointerMove={(event) => {
        if (!dragRef.current) return;
        const point = toPage(event.clientX, event.clientY);
        const x = Math.min(dragRef.current.x, point.x);
        const y = Math.min(dragRef.current.y, point.y);
        setPreview({
          x,
          y,
          width: Math.max(1, Math.abs(point.x - dragRef.current.x)),
          height: Math.max(1, Math.abs(point.y - dragRef.current.y)),
        });
      }}
      onPointerUp={(event) => {
        if (!dragRef.current) return;
        const point = toPage(event.clientX, event.clientY);
        const dragged = Math.hypot(point.x - dragRef.current.x, point.y - dragRef.current.y);
        if (dragged < DRAG_THRESHOLD_MM) {
          onComplete({
            rect: placePictureRect(
              pageWidthMm,
              pageHeightMm,
              native.widthMm,
              native.heightMm,
              dragRef.current,
            ),
            preserveAspectRatio: true,
          });
        } else {
          const x = Math.min(dragRef.current.x, point.x);
          const y = Math.min(dragRef.current.y, point.y);
          onComplete({
            rect: {
              x,
              y,
              width: Math.max(4, Math.abs(point.x - dragRef.current.x)),
              height: Math.max(4, Math.abs(point.y - dragRef.current.y)),
            },
            preserveAspectRatio: false,
          });
        }
        dragRef.current = null;
        setPreview(null);
      }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 6,
        cursor: 'crosshair',
        outline: 'none',
      }}
    >
      {preview ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: `${(preview.x / pageWidthMm) * 100}%`,
            top: `${(preview.y / pageHeightMm) * 100}%`,
            width: `${(preview.width / pageWidthMm) * 100}%`,
            height: `${(preview.height / pageHeightMm) * 100}%`,
            border: tokens.border.thin,
            background: tokens.color.accent.subtle,
            opacity: 0.6,
          }}
        />
      ) : null}
    </div>
  );
}
